import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { getCurrentInstance, ref, Ref } from 'vue';
import { StoreSetupService } from '@/api/services/store-setup.service';
import MenuQrStickerSettingsModal from '@/components/modals/sticker/MenuQrStickerSettingsModal.vue';
import { useModal } from '@/composition/use-modal';
import { usePrinting } from '@/composition/use-printing';
import { useUi } from '@/composition/use-ui';
import { IMenuQrCopy } from '@/domain/menu-qr/menu-qr-copy';
import { MenuQrLayoutError } from '@/domain/menu-qr/menu-qr-layout';
import { TMenuQrPosterSize } from '@/domain/menu-qr/menu-qr-poster';
import { IMenuQrTarget } from '@/domain/menu-qr/menu-qr-targets';
import { AlertType } from '@/interfaces/IAlert';
import { loadOptionalImage } from '@/lib/print/menu-qr-canvas';
import {
  canvasToPngBlob,
  renderMenuQrPoster,
} from '@/lib/print/menu-qr-poster-renderer';
import { MenuQrStickerPrinter } from '@/lib/print/MenuQrStickerPrinter';
import { MenuQrStickerSettings } from '@/lib/print/MenuQrStickerSettings';
import { useRestaurantStore } from '@/stores/restaurant.store';

export interface IMenuQrExportInput {
  targets: Ref<IMenuQrTarget[]>;
  skippedTables: Ref<string[]>;
  size: Ref<TMenuQrPosterSize>;
  copy: Ref<IMenuQrCopy | null>;
  logoUrl: Ref<string | null>;
  zipName: Ref<string>;
}

export interface IMenuQrProgress {
  done: number;
  total: number;
}

export interface IUseMenuQrExport {
  downloading: Ref<boolean>;
  progress: Ref<IMenuQrProgress | null>;
  download(): Promise<void>;
  printingStickers: Ref<boolean>;
  printStickers(): Promise<void>;
  openStickerSettings(): void;
}

function yieldToUi() {
  return new Promise<void>((resolve) => setTimeout(resolve));
}

/** The sticker always carries a title, even when the poster design draws none. */
function stickerTitleFor(target: IMenuQrTarget, copy: IMenuQrCopy): string {
  if (target.kind === 'table') return target.caption;
  if (target.kind === 'takeaway') return copy.takeAwayTitle;

  return copy.genericTitle;
}

/** Zip download of the selected posters, plus the platform-admin sticker printing. */
export function useMenuQrExport(input: IMenuQrExportInput): IUseMenuQrExport {
  const ui = useUi();
  const modal = useModal();
  const printing = usePrinting();
  const restaurantStore = useRestaurantStore();
  const proxy = getCurrentInstance()!.proxy;
  const downloading = ref(false);
  const progress = ref<IMenuQrProgress | null>(null);
  const printingStickers = ref(false);

  function t(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  async function download() {
    const targets = input.targets.value;
    const copy = input.copy.value;

    if (downloading.value || !targets.length || !copy) return;

    // Snapshot the options so changes made while generating cannot mix into the zip.
    const size = input.size.value;
    const zipName = input.zipName.value;
    const skippedCount = input.skippedTables.value.length;
    const restaurantId = restaurantStore.restaurant?.id ?? null;

    downloading.value = true;
    progress.value = { done: 0, total: targets.length };

    try {
      // Resolve the logo once for the whole batch; a failure is reported once and
      // the posters are rendered without it (no per-poster retries).
      let logoUrl = input.logoUrl.value;

      if (logoUrl && !(await loadOptionalImage(logoUrl))) {
        ui.showToast(AlertType.Info, t('qr.error.logo'));
        logoUrl = null;
      }

      const zip = new JSZip();
      const canvas = document.createElement('canvas');
      let done = 0;

      for (const target of targets) {
        const rendered = await renderMenuQrPoster({
          size,
          design: target.design,
          qrUrl: target.url,
          title: target.title,
          logoUrl,
          steps: copy.steps,
          canvas,
        });

        zip.file(target.zipPath, await canvasToPngBlob(rendered));
        done++;
        progress.value = { done, total: targets.length };
        await yieldToUi();
      }

      saveAs(await zip.generateAsync({ type: 'blob' }), zipName);

      // Ticks «Poner el QR en las mesas» on the setup checklist; never throws.
      if (restaurantId) void StoreSetupService.markQrDone(restaurantId);

      if (skippedCount > 0) {
        ui.showToast(
          AlertType.Info,
          t('qr.targets.tablesWithoutToken', { count: skippedCount })
        );
      }
    } catch (error) {
      console.error(error);
      ui.showToast(AlertType.Error, t('qr.failed'));
    } finally {
      downloading.value = false;
      progress.value = null;
    }
  }

  function printFailureMessage(
    error: unknown,
    printed: number,
    total: number,
    title: string
  ): string {
    if (error instanceof MenuQrLayoutError) {
      return t(
        error.code === 'empty-area'
          ? 'qr.stickers.layoutEmpty'
          : 'qr.stickers.layoutNoRoom'
      );
    }

    if (printed === 0) return t('qr.stickers.failedOne', { title });

    return t('qr.stickers.failedPartial', { printed, total, title });
  }

  async function printStickers() {
    const targets = input.targets.value;
    const copy = input.copy.value;
    const printer = printing.stickerPrinter.value;

    if (printingStickers.value || !targets.length || !copy) return;

    if (!printer) {
      ui.showToast(AlertType.Error, t('qr.stickers.noPrinter'));
      return;
    }

    printingStickers.value = true;

    let printed = 0;

    try {
      const settings = MenuQrStickerSettings.loadSettings({
        legacyBranchId: restaurantStore.selectedBranch?.id,
      });
      const logoUrl = input.logoUrl.value;

      for (const target of targets) {
        const title = stickerTitleFor(target, copy);

        try {
          const zpl = await MenuQrStickerPrinter.buildZpl({
            qrUrl: target.url,
            title,
            logoUrl,
            settings,
            steps: copy.steps,
          });

          await printing.printZpl(printer, zpl);
          printed++;
        } catch (error) {
          console.error(error);
          ui.showToast(
            AlertType.Error,
            printFailureMessage(error, printed, targets.length, title)
          );
          return;
        }
      }

      ui.showToast(
        AlertType.Success,
        t('qr.stickers.sent', { count: targets.length })
      );
    } catch (error) {
      console.error(error);
      ui.showToast(AlertType.Error, t('qr.stickers.failed'));
    } finally {
      printingStickers.value = false;
    }
  }

  function openStickerSettings() {
    modal.show(
      MenuQrStickerSettingsModal,
      { legacyBranchId: restaurantStore.selectedBranch?.id ?? null },
      {
        name: 'menu-qr-sticker-settings',
        adaptive: true,
        classes: 'dialog--height-limit',
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 820,
        clickToClose: false,
      }
    );
  }

  return {
    downloading,
    progress,
    download,
    printingStickers,
    printStickers,
    openStickerSettings,
  };
}
